import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Card, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Button, Stack, Typography,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Chip, OutlinedInput, type SelectChangeEvent, CardContent, Skeleton,
  Pagination, useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PageHeader } from "../components/PageHeader.js";
import { DialogHelp } from "../components/DialogHelp.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { EmptyState } from "../components/EmptyState.js";
import { useApi } from "../hooks/useApi.js";
import { type RowActionItem } from "../components/DataGridRowActionsMenu.js";
import { RowQuickActions } from "../components/RowQuickActions.js";
import type { Service } from "@kappa-v2/contracts";
import { EnumMonthType, EnumServiceType, EnumPaymentFrequencyType } from "@kappa-v2/contracts";
import { formLabel } from "../lib/labels.js";
import { useTranslation } from "react-i18next";

// Solo i valori (già chiavi i18n form.*): l'etichetta si ottiene con formLabel(value).
const SERVICE_TYPE_VALUES = Object.values(EnumServiceType);
const FREQUENCY_VALUES = Object.values(EnumPaymentFrequencyType);
const MONTH_VALUES = Object.values(EnumMonthType);

const monthLabel = (value: string): string => formLabel(value);

const MONTHLY = "form.paymentFrequency.mothly_fee";
const PAGE_SIZE = 15;

interface FormValues {
  name: string;
  description: string;
  price: number;
  paymentFrequency: string;
  serviceType: string;
  deducibleFixValue: number;
  frequencies: string[];
  isDeducible: boolean;
  emailNotification: boolean;
  accorpateInReport: boolean;
}

const EMPTY: FormValues = {
  name: "",
  description: "",
  price: 0,
  paymentFrequency: MONTHLY,
  serviceType: "form.service.horse",
  deducibleFixValue: 0,
  frequencies: [],
  isDeducible: false,
  emailNotification: false,
  accorpateInReport: false
};

function toPayload(values: FormValues): Omit<Service, "_id" | "tenantId" | "createdAt" | "updatedAt"> {
  const isMonthly = values.paymentFrequency === MONTHLY;
  return {
    name: values.name.trim(),
    description: values.description.trim(),
    price: Number(values.price),
    paymentFrequency: values.paymentFrequency,
    serviceType: values.serviceType,
    emailNotification: values.emailNotification,
    isDeducible: values.isDeducible,
    deducibleFixValue: Math.max(0, Number(values.deducibleFixValue) || 0),
    accorpateInReport: values.accorpateInReport,
    frequencies: isMonthly ? [...values.frequencies] : []
  };
}

function serviceToFormValues(s: Service): FormValues {
  return {
    name: s.name ?? "",
    description: s.description ?? "",
    price: s.price ?? 0,
    paymentFrequency: s.paymentFrequency || MONTHLY,
    serviceType: s.serviceType || "form.service.horse",
    deducibleFixValue: s.deducibleFixValue ?? 0,
    frequencies: Array.isArray(s.frequencies) ? s.frequencies : [],
    isDeducible: Boolean(s.isDeducible),
    emailNotification: Boolean(s.emailNotification),
    accorpateInReport: Boolean(s.accorpateInReport)
  };
}

export function ServicesPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const schema = Yup.object({
    name: Yup.string().trim().required(t("services.form.nameRequired")),
    description: Yup.string().trim().required(t("services.form.descriptionRequired")),
    price: Yup.number().min(0).required(t("services.form.priceRequired")),
    paymentFrequency: Yup.string().required(t("services.form.frequencyRequired")),
    serviceType: Yup.string().required(t("services.form.typeRequired")),
    deducibleFixValue: Yup.number().min(0).required(),
    frequencies: Yup.array().of(Yup.string()).when("paymentFrequency", {
      is: MONTHLY,
      then: (s) => s.min(1, t("services.form.monthsRequired")),
      otherwise: (s) => s
    })
  });
  const [rows, setRows] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Service | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    call<Service[] | { data: Service[] }>("/api/v1/services")
      .then((r) => setRows(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const body = toPayload(values);
        if (editTarget?._id) {
          await call<Service>(`/api/v1/services/${editTarget._id}`, {
            method: "PUT",
            body
          });
        } else {
          await call<Service>("/api/v1/services", { method: "POST", body });
        }
        setDialogOpen(false);
        void fetchAll();
      } finally {
        setSaving(false);
      }
    }
  });

  const openAdd = () => {
    setEditTarget(null);
    formik.resetForm({ values: EMPTY });
    setDialogOpen(true);
  };

  const openEdit = (s: Service) => {
    setEditTarget(s);
    formik.resetForm({ values: serviceToFormValues(s) });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/services/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null);
    void fetchAll();
  };

  const filtered = useMemo(() => (
    rows.filter((r) =>
      `${r.name} ${r.description}`.toLowerCase().includes(search.toLowerCase())
    )
  ), [rows, search]);

  const isMonthly = formik.values.paymentFrequency === MONTHLY;

  const buildActionItems = useCallback((row: Service): RowActionItem[] => [
    {
      key: "edit",
      label: t("services.editAction"),
      description: t("services.editActionDesc"),
      icon: <EditIcon fontSize="small" />,
      onClick: () => openEdit(row)
    },
    {
      key: "delete",
      label: t("services.deleteAction"),
      description: t("services.deleteActionDesc"),
      icon: <DeleteIcon fontSize="small" />,
      destructive: true,
      dividerBefore: true,
      onClick: () => setDeleteTarget(row)
    }
  ], []);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("services.colServiceCourse"),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Service>) => (
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography variant="body2" fontWeight={600} noWrap title={params.row.name}>
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div" title={params.row.description}>
            {params.row.description}
          </Typography>
        </Box>
      )
    },
    {
      field: "serviceType",
      headerName: t("services.type"),
      width: 140,
      renderCell: (params: GridRenderCellParams<Service>) => (
        <Chip
          size="small"
          label={formLabel(params.row.serviceType)}
          variant="outlined"
          sx={{ fontSize: "0.72rem" }}
        />
      )
    },
    {
      field: "price",
      headerName: t("services.price"),
      width: 110,
      renderCell: (params: GridRenderCellParams<Service>) => (
        <Typography variant="body2" fontWeight={600} color="primary.dark">
          € {params.row.price.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: "paymentFrequency",
      headerName: t("services.frequency"),
      width: 130,
      renderCell: (params: GridRenderCellParams<Service>) => (
        <Chip
          size="small"
          label={formLabel(params.row.paymentFrequency)}
          variant="outlined"
          sx={{ fontSize: "0.72rem" }}
        />
      )
    },
    {
      field: "isDeducible",
      headerName: t("services.deductible"),
      width: 100,
      renderCell: (params: GridRenderCellParams<Service>) => (
        <Typography variant="caption" color={params.row.isDeducible ? "success.main" : "text.disabled"}>
          {params.row.isDeducible ? t("common.yes") : t("common.no")}
        </Typography>
      )
    },
    {
      field: "actions",
      headerName: "",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Service>) => (
        <RowQuickActions
          ariaLabel={t("services.actionsAria", { name: params.row.name })}
          quickKeys={["edit"]}
          items={[
            {
              key: "edit",
              label: "Modifica",
              description: "Apri il modulo servizio/corso.",
              icon: <EditIcon fontSize="small" />,
              onClick: () => openEdit(params.row)
            },
            {
              key: "delete",
              label: "Elimina",
              description: "Rimuove il servizio dal catalogo.",
              icon: <DeleteIcon fontSize="small" />,
              destructive: true,
              dividerBefore: true,
              onClick: () => setDeleteTarget(params.row)
            }
          ]}
        />
      )
    }
  ];

  const handleMonthChange = (event: SelectChangeEvent<string[]>) => {
    const v = event.target.value;
    formik.setFieldValue(
      "frequencies",
      typeof v === "string" ? v.split(",").filter(Boolean) : v
    );
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("services.title")} subtitle={t("services.subtitle")} actionLabel={t("services.newService")} onAction={openAdd} onRefresh={fetchAll} />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
            <TextField
              size="small"
              placeholder={t("services.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                )
              }}
              sx={{ width: { xs: "100%", sm: 320 } }}
            />
          </Box>
          {!loading && filtered.length === 0 ? (
            <EmptyState title={t("services.none")} actionLabel={t("services.addService")} onAction={openAdd} />
          ) : isMobile ? (
            <ServicesMobileCards
              rows={filtered}
              loading={loading}
              buildActionItems={buildActionItems}
            />
          ) : (
            <DataGrid
              rows={filtered}
              columns={columns}
              getRowId={(r) => r._id ?? Math.random().toString()}
              loading={loading}
              autoHeight
              rowHeight={60}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
              }}
            />
          )}
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            {editTarget ? t("services.editTitle", { name: editTarget.name }) : t("services.newTitle")}
            <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.services") }]} />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("services.form.nameLabel")}
                  {...formik.getFieldProps("name")}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" error={formik.touched.serviceType && Boolean(formik.errors.serviceType)}>
                  <InputLabel>{t("services.form.typeLabel")}</InputLabel>
                  <Select label={t("services.form.typeLabel")} {...formik.getFieldProps("serviceType")}>
                    {SERVICE_TYPE_VALUES.map((value) => (
                      <MenuItem key={value} value={value}>
                        {formLabel(value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("services.form.deductibleQuotaLabel")}
                  type="number"
                  {...formik.getFieldProps("deducibleFixValue")}
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText={t("services.form.deductibleQuotaHelper")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("services.form.priceLabel")}
                  type="number"
                  {...formik.getFieldProps("price")}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={formik.touched.price && formik.errors.price}
                  inputProps={{ step: 0.01, min: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("services.form.descriptionLabel")}
                  multiline
                  rows={3}
                  {...formik.getFieldProps("description")}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("services.form.frequencyLabel")}</InputLabel>
                  <Select
                    label={t("services.form.frequencyLabel")}
                    name="paymentFrequency"
                    value={formik.values.paymentFrequency}
                    onChange={(e) => {
                      const v = e.target.value;
                      formik.setFieldValue("paymentFrequency", v);
                      if (v !== MONTHLY) formik.setFieldValue("frequencies", []);
                    }}
                  >
                    {FREQUENCY_VALUES.map((value) => (
                      <MenuItem key={value} value={value}>
                        {formLabel(value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {isMonthly ? (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" error={formik.touched.frequencies && Boolean(formik.errors.frequencies)}>
                    <InputLabel id="service-months-label">{t("services.form.monthsLabel")}</InputLabel>
                    <Select<string[]>
                      labelId="service-months-label"
                      multiple
                      value={formik.values.frequencies}
                      onChange={handleMonthChange}
                      input={<OutlinedInput label={t("services.form.monthsLabel")} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {(selected as string[]).map((val) => (
                            <Chip
                              key={val}
                              size="small"
                              label={monthLabel(val)}
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={() =>
                                formik.setFieldValue(
                                  "frequencies",
                                  formik.values.frequencies.filter((m: string) => m !== val)
                                )
                              }
                              deleteIcon={<CancelIcon onMouseDown={(e) => e.stopPropagation()} />}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {MONTH_VALUES.map((value) => (
                        <MenuItem key={value} value={value}>
                          {formLabel(value)}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.frequencies && typeof formik.errors.frequencies === "string" ? (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {formik.errors.frequencies}
                      </Typography>
                    ) : null}
                  </FormControl>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.isDeducible}
                        onChange={(e) => formik.setFieldValue("isDeducible", e.target.checked)}
                      />
                    }
                    label={t("services.form.courseDeductible")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.emailNotification}
                        onChange={(e) => formik.setFieldValue("emailNotification", e.target.checked)}
                      />
                    }
                    label={t("services.form.notifyDelays")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.accorpateInReport}
                        onChange={(e) => formik.setFieldValue("accorpateInReport", e.target.checked)}
                      />
                    }
                    label={t("services.form.extraInReport")}
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? t("common.saving") : editTarget ? t("common.save") : t("common.add")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("services.deleteConfirm", { name: deleteTarget?.name })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}

interface ServicesMobileCardsProps {
  rows: Service[];
  loading: boolean;
  buildActionItems: (row: Service) => RowActionItem[];
}

function ServicesMobileCards({
  rows,
  loading,
  buildActionItems
}: ServicesMobileCardsProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const slice = useMemo(() => {
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page, pageCount]);

  useEffect(() => {
    setPage(1);
  }, [rows.length]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  if (loading) {
    return (
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={132} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {slice.map((row) => {
          const id = row._id ?? row.name;
          return (
            <Card
              key={id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: "rgba(0,0,0,.08)",
                bgcolor: "background.paper"
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} component="div">
                      {row.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {row.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        label={formLabel(row.serviceType)}
                        variant="outlined"
                        sx={{ fontSize: "0.72rem" }}
                      />
                      <Chip
                        size="small"
                        label={formLabel(row.paymentFrequency)}
                        variant="outlined"
                        sx={{ fontSize: "0.72rem" }}
                      />
                    </Stack>
                    <Typography variant="body2" fontWeight={600} color="primary.dark">
                      € {row.price.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={row.isDeducible ? "success.main" : "text.secondary"}
                    >
                      {row.isDeducible ? t("services.deductibleYes") : t("services.deductibleNo")}
                    </Typography>
                  </Box>
                  <RowQuickActions
                    ariaLabel={t("services.actionsAria", { name: row.name })}
                    quickKeys={["edit"]}
                    items={buildActionItems(row)}
                  />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      {rows.length > PAGE_SIZE ? (
        <Box sx={{ display: "flex", justifyContent: "center", pb: 2, px: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
            siblingCount={0}
            boundaryCount={1}
          />
        </Box>
      ) : null}
    </Stack>
  );
}
